import { Injectable } from '@angular/core';
import * as dcbm from '@leandredev/dcbm-ng-lib';

import { BehaviorSubject, Observable, filter, interval, map, switchMap, takeUntil } from 'rxjs';

import { SignInJwtService } from 'src/app/services/signIn-jwt/signIn-jwt.service';

import { ApiHelper, IHttpOption } from '@leandredev/dcbm-ng-lib';
import { IHttpResult, interfaces as I } from '@leandredev/dcbm-common-lib';
import { DepartmentService } from '../department/department.service';
import { ApiService } from '../api/api.service';
import { ProfileService } from '../profile/profile.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

type UnionMessage = I.IMessage;
@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private service: dcbm.service.Chat;

  public fileDownloadServiceUrl: string;

  private profile: I.IUserAdmin;

  public enabledChat: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public unreadMessagesCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public messageFileRequested: BehaviorSubject<I.IMessage> = new BehaviorSubject<I.IMessage>(null);

  constructor(
    private http: HttpClient,
    private signInService: SignInJwtService,
    private departmentService: DepartmentService,
    private apiService: ApiService,
    private profileService: ProfileService
  ) {
    this.service = new dcbm.service.Chat(this.http, true);
    this.service.urlCollection = `${environment.data.api}chat/`;

    this.signInService.connected.subscribe((isConnected) => {
      if (isConnected) {
        this.profileService.profile.subscribe((userAdmin) => {
          this.profile = userAdmin;
        });

        this.departmentService
          .getAll('*')
          .pipe(
            ApiHelper.resultToArr('liste de service inconnue'),
            map((services: I.IDcbmService[]) => {
              return services.map((s) => {
                return s.manager;
              });
            }),
            map((admins: string[]) => {
              if (admins.includes(`${this.profile._id}`)) {
                this.enabledChat.next(true);
                this.startFindUnreadMessagesInterval();
              } else {
                this.enabledChat.next(false);
              }
              return this.enabledChat;
            }),
            switchMap(() => this.getUnreadMessageCountOfAll())
          )
          .subscribe();
      }
    });

    this.fileDownloadServiceUrl = `${environment.data.appApi}fileAdminService/local`;
  }
  // API methods =======================================================================================================

  private _sendMessageFiledRequested<T extends I.IMessage, U extends I.IStringList>(
    body: U,
    query: object,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.sendMessageFiledRequested(body, query, options);
  }

  private _setActivated<T extends I.IDeviceActivationRequest>(query: object, messageId: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.setActivated(query, messageId, options);
  }

  private _sendMessage<T extends I.IMessage, U extends I.IMessage>(body: U, query: object, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.sendMessage(body, query, options);
  }

  private _getMessageOf<T extends I.IMessageList>(
    query: object,
    userId: string,
    limit: number,
    skip: number,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.getMessageOf(query, userId, limit, skip, options);
  }

  private _getUnreadMessageOf<T extends I.IMessageList>(query: object, userId: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.getUnreadMessageOf(query, userId, options);
  }

  private _getUnreadMessageCountOfAll<T extends number>(query: object, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.getUnreadMessageCountOfAll(query, options);
  }

  private _setMessageHasBeenRead<T extends I.IMessageList, U extends { value: string[] }>(
    body: U,
    query: object,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.setMessageHasBeenRead(body, query, options);
  }

  private _getUnreadMessageOfAll<T extends I.IMessageStackList, U extends { value: string[] }>(
    body: U,
    query: object,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.getUnreadMessageOfAll(body, query, options);
  }

  // ===================================================================================================================

  public getUnreadMessageOfAll(clientIds: string[]): Observable<I.IMessageStack[]> {
    return this._getUnreadMessageOfAll({ value: clientIds }, {}).pipe(
      ApiHelper.resultToObjWEmptyError('error'),
      map((result) => result.value)
    );
  }

  private startFindUnreadMessagesInterval(): void {
    interval(1000 * 60)
      .pipe(
        takeUntil(this.signInService.connected.pipe(filter((isConnected) => !isConnected))),
        switchMap(() => this.getUnreadMessageCountOfAll())
      )
      .subscribe();
  }

  public getClient(): Observable<I.IUserClient[]> {
    return this.apiService.collections.UserClient.get().pipe(ApiHelper.resultToArrWEmptyError(''));
  }

  protected setMessageOf(message: UnionMessage): UnionMessage {
    message.admin = this.profile._id;
    message.postedAt = new Date();
    message.sender = 'admin';
    return message;
  }

  public sendMessageFiledRequested(message: I.IMessage): Observable<I.IMessage> {
    this.setMessageOf(message);

    return this._sendMessage(message, {}).pipe(
      ApiHelper.resultToObjWEmptyError(''),
      map((v) => {
        this.messageFileRequested.next(v);
        return v;
      })
    );
  }

  public setActivated(message: I.IDeviceActivationRequest): Observable<I.IDeviceActivationRequest> {
    return this._setActivated(null, message._id as string).pipe(ApiHelper.resultToObj('error'));
  }

  public sendMessage(message: I.IMessage): Observable<I.IMessage> {
    this.setMessageOf(message);
    return this._sendMessage(message, {}).pipe(ApiHelper.resultToObjWEmptyError('Error'));
  }

  public getMessageOf(user: I.IUserClient, limit = 5, skip = 0): Observable<I.IMessage[]> {
    return this._getMessageOf({}, user._id, limit, skip).pipe(
      ApiHelper.resultToObj('error'),
      map((messageList) => messageList.value)
    );
  }

  public getUnreadMessageOf(client: I.IUserClient): Observable<I.IMessage[]> {
    return this._getUnreadMessageOf(null, client._id).pipe(
      ApiHelper.resultToObj('error'),
      map((messageList) => messageList.value)
    );
  }

  public getUnreadMessageCountOfAll(): Observable<number> {
    return this._getUnreadMessageCountOfAll(null).pipe(
      ApiHelper.resultToObj('error'),
      map((r) => {
        this.unreadMessagesCount.next(r);
        return r;
      })
    );
  }

  public setHasBeenRead(messages: I.IMessage[]): Observable<I.IMessage[]> {
    const messageIds: string[] = messages.map((e) => e._id);
    return this._setMessageHasBeenRead({ value: messageIds }, null).pipe(
      ApiHelper.resultToObj('Error dans la mise a jour'),
      map((res) => res.value),
      map((m) => {
        messages.forEach((v) => (v.seenAt = new Date()));
        this.getUnreadMessageCountOfAll().subscribe();
        return m;
      })
    );
  }
}
